import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import Toast from '../components/Toast';

function BackupManagement() {
  const { darkMode } = useTheme();
  const [backups, setBackups] = useState([]);
  const [settings, setSettings] = useState({
    enabled: false,
    frequency: 'daily',
    time: '02:00',
    retention_days: 30
  });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => { fetchBackups(); fetchSettings(); }, []);

  const fetchBackups = async () => {
    try {
      const response = await axios.get('/api/admin/backups');
      setBackups(response.data);
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to load backups', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/admin/backups/settings');
      if (response.data) {
        setSettings(response.data);
      }
    } catch (err) {
      console.error('Failed to load backup settings:', err);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    setToast(null);

    try {
      await axios.post('/api/admin/backups/create');
      setToast({ message: 'Backup created successfully', type: 'success' });
      await fetchBackups();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to create backup', type: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = async (filename) => {
    try {
      const response = await axios.get(`/api/admin/backups/download/${filename}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setToast({ message: 'Failed to download backup', type: 'error' });
    }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete backup ${filename}?`)) {
      return;
    }

    try {
      await axios.delete(`/api/admin/backups/${filename}`);
      setToast({ message: 'Backup deleted successfully', type: 'success' });
      await fetchBackups();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to delete backup', type: 'error' });
    }
  };

  const updateSettings = async (e) => {
    e.preventDefault();
    setToast(null);

    try {
      await axios.put('/api/admin/backups/settings', settings);
      setToast({ message: 'Backup settings updated successfully', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update settings', type: 'error' });
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Loading backup management...</div>;
  }

  return (
    <div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="content-header">
        <div>
          <h1 style={{ margin: '0 0 5px 0' }}>Backup Management</h1>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Manage database and code backups
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={createBackup}
          disabled={creating}
        >
          {creating ? 'Creating Backup...' : 'Create Backup Now'}
        </button>
      </div>
      <div className="content-body">
        {/* Backup Settings */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header">
            <h3>Scheduled Backup Settings</h3>
          </div>
          <div className="card-body">
            <form onSubmit={updateSettings}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={settings.enabled}
                      onChange={(e) => setSettings({...settings, enabled: e.target.checked})}
                      style={{
                        marginRight: '10px',
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{ fontSize: '15px', fontWeight: '500' }}>Enable Scheduled Backups</span>
                  </label>
                </div>

                <div className="form-group">
                  <label>Backup Frequency</label>
                  <select
                    value={settings.frequency}
                    onChange={(e) => setSettings({...settings, frequency: e.target.value})}
                    disabled={!settings.enabled}
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Backup Time (24h format)</label>
                  <input
                    type="time"
                    value={settings.time}
                    onChange={(e) => setSettings({...settings, time: e.target.value})}
                    disabled={!settings.enabled || settings.frequency === 'hourly'}
                  />
                </div>

                <div className="form-group">
                  <label>Retention Period (days)</label>
                  <input
                    type="number"
                    value={settings.retention_days}
                    onChange={(e) => setSettings({...settings, retention_days: parseInt(e.target.value)})}
                    min="1"
                    max="365"
                    disabled={!settings.enabled}
                  />
                  <small style={{ color: '#6b7280' }}>
                    Backups older than this will be automatically deleted
                  </small>
                </div>
              </div>

              <button type="submit" className="btn btn-primary" style={{ marginTop: '15px' }}>
                Save Settings
              </button>
            </form>
          </div>
        </div>
        {/* Backup List */}
        <div className="card">
          <div className="card-header">
            <h3>Available Backups ({backups.length})</h3>
          </div>
          <div className="card-body">
            {backups.length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((backup) => (
                    <tr key={backup.filename}>
                      <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{backup.filename}</td>
                      <td>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          background: backup.type === 'database' ? '#dbeafe' : '#fef3c7',
                          color: backup.type === 'database' ? '#1e40af' : '#92400e'
                        }}>
                          {backup.type === 'database' ? 'Database' : 'Code'}
                        </span>
                      </td>
                      <td>{formatBytes(backup.size)}</td>
                      <td>{formatDate(backup.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-sm"
                            onClick={() => downloadBackup(backup.filename)}
                            style={{ padding: '4px 12px', fontSize: '13px' }}
                          >
                            Download
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() => deleteBackup(backup.filename)}
                            style={{
                              padding: '4px 12px',
                              fontSize: '13px',
                              background: '#fee2e2',
                              color: '#991b1b'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <p>No backups found</p>
                <p style={{ fontSize: '14px', color: '#6b7280' }}>
                  Click "Create Backup Now" to create your first backup
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BackupManagement;
